import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProductGroups } from "../../../store/actions/productGroupActions";
import { getAllManufacturers, getAllCategories } from "../../../api/pagination";
import { allManufacturers, AllCategories } from "../../../store/actions/actions";

const useProductGroups = () => {
    const dispatch = useDispatch();
    const { productGroups = [], pagination: reduxPagination = {}, loading, error } = useSelector((state) => state.productGroups);
    const { all_manufacturers, all_categories } = useSelector((state) => state.redux);

    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState("date_created");
    const [sortOrder, setSortOrder] = useState("desc");
    const [mfgId, setMfgId] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [groupType, setGroupType] = useState("");
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [manufacturersList, setManufacturersList] = useState(all_manufacturers || []);
    const [categoriesList, setCategoriesList] = useState(all_categories || []);

    // Fetch Manufacturers via REST API on mount if not in Redux
    useEffect(() => {
        if (!all_manufacturers || all_manufacturers.length === 0) {
            getAllManufacturers(1, "", 1000, (res) => {
                if (res?.status === 200 && res?.data) {
                    const mfgArray = res.data.data || (Array.isArray(res.data) ? res.data : []);
                    setManufacturersList(mfgArray);
                    dispatch(allManufacturers(mfgArray));
                }
            });
        } else {
            setManufacturersList(all_manufacturers);
        }
    }, [all_manufacturers, dispatch]);

    // Fetch Categories via REST API on mount if not in Redux
    useEffect(() => {
        if (!all_categories || all_categories.length === 0) {
            getAllCategories(1, "", 1000, (res) => {
                if (res?.status === 200 && res?.data) {
                    const catArray = res.data.data || (Array.isArray(res.data) ? res.data : (res.data.categories || []));
                    setCategoriesList(catArray);
                    dispatch(AllCategories(catArray));
                }
            });
        } else {
            setCategoriesList(all_categories);
        }
    }, [all_categories, dispatch]);

    // Fetch product groups from backend API with multi-page support
    useEffect(() => {
        dispatch(fetchProductGroups(100, 1, sortBy, sortOrder));
    }, [dispatch, sortBy, sortOrder, refreshTrigger]);

    // Comprehensive client-side filtering across all loaded product groups
    const filteredProductGroups = useMemo(() => {
        if (!Array.isArray(productGroups)) return [];
        let result = [...productGroups];

        // 1. Group Type Filter
        if (groupType) {
            const targetType = groupType.toLowerCase().trim();
            result = result.filter((g) => {
                const rawType = String(g?.groupType || g?.type || g?.group_type || "").toLowerCase().trim();
                const isRelated = rawType === "related" || rawType === "related_products" || rawType === "related_group";
                const isBundle = rawType === "bundle" || rawType === "bundle_group";
                const isVariant = rawType === "variant" || rawType === "variant_group" || rawType === "variants" || (!isRelated && !isBundle);

                if (targetType === "variant") return isVariant;
                if (targetType === "related") return isRelated;
                if (targetType === "bundle") return isBundle;
                return rawType === targetType;
            });
        }

        // 2. Manufacturer Filter
        if (mfgId) {
            const targetMfg = String(mfgId).trim().toLowerCase();
            const matchedMfgObj = Array.isArray(manufacturersList) 
                ? manufacturersList.find(m => String(m?._id || m?.id || '').toLowerCase() === targetMfg || String(m?.name || m?.mfg_name || '').toLowerCase() === targetMfg)
                : null;
            const targetMfgName = (matchedMfgObj?.name || matchedMfgObj?.mfg_name || "").toLowerCase().trim();

            result = result.filter((g) => {
                const mId = String(
                    g?.mfg_id ||
                    g?.mfgId ||
                    g?.mfg?._id ||
                    g?.mfg?.id ||
                    g?.mfgDetails?._id ||
                    g?.mfgDetails?.id ||
                    g?.manufacturer?._id ||
                    g?.manufacturer?.id ||
                    g?.manufacturer ||
                    g?.mfg ||
                    ""
                ).trim().toLowerCase();

                const mName = String(
                    g?.mfg_name ||
                    g?.mfg?.name ||
                    g?.mfgDetails?.name ||
                    g?.manufacturer?.name ||
                    ""
                ).trim().toLowerCase();

                return (
                    mId === targetMfg ||
                    (targetMfgName && (mId === targetMfgName || mName === targetMfgName)) ||
                    (mName && mName === targetMfg)
                );
            });
        }

        // 3. Category Filter
        if (categoryId) {
            const targetCat = String(categoryId).trim().toLowerCase();
            const matchedCatObj = Array.isArray(categoriesList)
                ? categoriesList.find(c => String(c?._id || c?.id || '').toLowerCase() === targetCat || String(c?.categoryName || c?.name || '').toLowerCase() === targetCat)
                : null;
            const targetCatName = (matchedCatObj?.categoryName || matchedCatObj?.name || "").toLowerCase().trim();

            result = result.filter((g) => {
                const cId = String(
                    g?.categoryId ||
                    g?.category_id ||
                    g?.category?._id ||
                    g?.category?.id ||
                    g?.subCategoryId ||
                    g?.subCategory?._id ||
                    g?.subCategory?.id ||
                    g?.category ||
                    ""
                ).trim().toLowerCase();

                const cName = String(
                    g?.categoryName ||
                    g?.category?.categoryName ||
                    g?.category?.name ||
                    ""
                ).trim().toLowerCase();

                return (
                    cId === targetCat ||
                    (targetCatName && (cId === targetCatName || cName === targetCatName)) ||
                    (cName && cName === targetCat)
                );
            });
        }

        // 4. Global Multi-term Search across all fields and variant items
        if (searchTerm && searchTerm.trim()) {
            const terms = searchTerm.trim().toLowerCase().split(/\s+/).filter(Boolean);
            result = result.filter((g) => {
                const name = String(g?.name || "").toLowerCase();
                const slug = String(g?.slug || "").toLowerCase();
                const desc = String(g?.description || g?.long_description || "").toLowerCase();
                const type = String(g?.groupType || g?.type || g?.group_type || "").toLowerCase();
                const mfg = String(g?.mfg_name || g?.mfg?.name || g?.mfgDetails?.name || g?.manufacturer?.name || "").toLowerCase();
                const cat = String(g?.categoryName || g?.category?.categoryName || g?.category?.name || "").toLowerCase();
                const id = String(g?._id || g?.id || g?.groupId || "").toLowerCase();

                let variantsText = "";
                const rawVariants = Array.isArray(g?.variants) ? g.variants : (Array.isArray(g?.products) ? g.products : (Array.isArray(g?.items) ? g.items : []));
                if (rawVariants.length > 0) {
                    variantsText = rawVariants.map((v) => {
                        return `${v?.sku || ""} ${v?.mfgPartNumber || v?.partNumber || ""} ${v?.name || v?.item_title || v?.title || ""} ${v?.description || ""}`;
                    }).join(" ").toLowerCase();
                }

                const searchableText = `${name} ${slug} ${desc} ${type} ${mfg} ${cat} ${id} ${variantsText}`;

                return terms.every((t) => searchableText.includes(t));
            });
        }

        // Sort results across all filtered items
        result.sort((a, b) => {
            let valA = a[sortBy] ?? a.date_created ?? 0;
            let valB = b[sortBy] ?? b.date_created ?? 0;

            if (sortBy === "name" || sortBy === "slug") {
                valA = String(valA || "").toLowerCase();
                valB = String(valB || "").toLowerCase();
                return sortOrder === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }

            const dateA = new Date(valA).getTime() || 0;
            const dateB = new Date(valB).getTime() || 0;
            return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
        });

        return result;
    }, [productGroups, groupType, mfgId, categoryId, searchTerm, sortBy, sortOrder, manufacturersList, categoriesList]);

    const handleTableChange = (tablePagination, filters, sorter) => {
        if (tablePagination?.current && tablePagination.current !== page) {
            setPage(tablePagination.current);
        }
        if (tablePagination?.pageSize && tablePagination.pageSize !== pageSize) {
            setPageSize(tablePagination.pageSize);
            setPage(1);
        }
        if (sorter && sorter.field) {
            setSortBy(sorter.field);
            setSortOrder(sorter.order === "ascend" ? "asc" : "desc");
            setPage(1);
        }
    };

    const handleSearchChange = (val) => {
        setSearchTerm(val !== undefined ? val : "");
        setPage(1);
    };

    const handleGroupTypeChange = (val) => {
        setGroupType(val || "");
        setPage(1);
    };

    const handleMfgChange = (val) => {
        setMfgId(val || "");
        setPage(1);
    };

    const handleCategoryChange = (val) => {
        setCategoryId(val || "");
        setPage(1);
    };

    const resetFilters = useCallback(() => {
        setSearchTerm("");
        setGroupType("");
        setMfgId("");
        setCategoryId("");
        setPage(1);
    }, []);

    const refreshData = useCallback(() => {
        setRefreshTrigger((prev) => prev + 1);
    }, []);

    const isFiltered = Boolean(searchTerm || mfgId || categoryId || groupType);

    const totalItems = filteredProductGroups.length;
    const totalPages = Math.ceil(totalItems / (pageSize || 10)) || 1;

    const tablePagination = {
        totalItems: totalItems,
        currentPage: page,
        pageSize: pageSize,
        totalPages: totalPages
    };

    return {
        productGroups: filteredProductGroups,
        rawGroups: Array.isArray(productGroups) ? productGroups : [],
        pagination: tablePagination,
        loading,
        error,
        pageSize,
        setPageSize,
        page,
        setPage,
        sortBy,
        setSortBy,
        sortOrder,
        setSortOrder,
        mfgId,
        setMfgId: handleMfgChange,
        categoryId,
        setCategoryId: handleCategoryChange,
        searchTerm,
        setSearchTerm: handleSearchChange,
        groupType,
        setGroupType: handleGroupTypeChange,
        resetFilters,
        refreshData,
        isFiltered,
        manufacturersList,
        categoriesList,
        handleTableChange
    };
};

export default useProductGroups;
